<template>
    <CRow>
        <CCol>
            <CCard v-if="entity">
                <CCardHeader>
                    <strong>{{ entity.title }}</strong>
                </CCardHeader>

                <CCardBody>
                    <GoodsTabLinks />

                    <div class="flex items-center mt-4">
                        <span class="w-1/3 font-bold">Название</span>
                        <span class="w-2/3">{{ entity.title }}</span>
                    </div>

                    <div class="flex items-center mt-4">
                        <span class="w-1/3 font-bold">Краткое описание</span>
                        <span class="w-2/3">{{ entity.description }}</span>
                    </div>

                    <div class="flex items-center mt-4">
                        <span class="w-1/3 font-bold">Количество (шт)</span>
                        <span class="w-2/3">{{ entity.quantity }}</span>
                    </div>

                    <div class="flex items-center mt-4">
                        <span class="w-1/3 font-bold">Цена</span>
                        <span class="w-2/3">{{ entity.price }}</span>
                    </div>

                    <div class="flex items-center mt-4">
                        <span class="w-1/3 font-bold">Значения ставки НДС</span>
                        <span class="w-2/3">{{ vat }}</span>
                    </div>

                    <div class="flex items-center mt-4">
                        <span class="w-1/3 font-bold">Шаг для добавления в корзину</span>
                        <span class="w-2/3">{{ entity.weight_step }}</span>
                    </div>

                    <div class="flex items-center mt-4">
                        <span class="w-1/3 font-bold">Единицы измерения</span>
                        <span class="w-2/3">{{ unitCodeTitle[entity.weight_unit] }}</span>
                    </div>

                    <div class="flex items-center mt-4">
                        <span class="w-1/3 font-bold">Вес</span>
                        <span class="w-2/3">{{ entity.weight }}</span>
                    </div>

                    <div class="flex items-center mt-4">
                        <span class="w-1/3 font-bold">Сертификация</span>
                        <span class="w-2/3">{{ certRequiredTitle }}</span>
                    </div>

                    <div class="flex items-start mt-4">
                        <span class="w-1/3 font-bold">Ссылки на сертификаты</span>
                        <span class="w-2/3">
                            <template v-if="entity.cert_urls && entity.cert_urls.length">
                                <a
                                    v-for="url in entity.cert_urls"
                                    :key="url"
                                    class="block"
                                    :href="url"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >{{ url }}</a>
                            </template>
                            <template v-else>Нет ссылок</template>
                        </span>
                    </div>

                    <div class="flex items-start mt-4">
                        <span class="w-1/3 font-bold">Картинки</span>
                        <div class="flex flex-wrap w-2/3 mt-2 -ml-2">
                            <div
                                v-for="item in entity.pictures"
                                :key="item"
                                class="w-1/4 p-2"
                            >
                                <div
                                    class="relative w-full overflow-hidden bg-no-repeat bg-cover rounded-md shadow-md group-hover"
                                    :style="{
                                        paddingTop: '100%',
                                        backgroundImage: `url(${getStaticPath(item)})`,
                                        backgroundPosition: 'center center',
                                    }"
                                >
                                    <div
                                        v-if="entity.main_picture === item"
                                        class="absolute inset-x-0 bottom-0 flex items-center h-10 px-1 text-green-500 bg-black bg-opacity-75"
                                    >
                                        <CIcon :height="20" :content="cilCheckCircle" />
                                        <span class="ml-2 text-xs">Основная картинка</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex items-center mt-4">
                        <span class="w-1/3 font-bold">Популярный</span>
                        <span class="w-2/3">
                            <CBadge :color="entity.popular ? 'success' : 'secondary'">
                                <span class="px-1">{{ entity.popular ? "Да" : "Нет" }}</span>
                            </CBadge>
                        </span>
                    </div>

                    <div class="flex items-center mt-4">
                        <span class="w-1/3 font-bold">Активен</span>
                        <span class="w-2/3">
                            <CBadge :color="entity.enabled ? 'success' : 'secondary'">
                                <span class="px-1">{{
                                    entity.enabled ? "Активен" : "Неактивен"
                                }}</span>
                            </CBadge>
                        </span>
                    </div>

                    <div class="flex items-center mt-4">
                        <span class="w-1/3 font-bold">Положение в списке</span>
                        <span class="w-2/3">{{ entity.position }}</span>
                    </div>
                </CCardBody>

                <CCardFooter>
                    <CButton
                        color="secondary"
                        @click="$router.push(`/grocers/${$route.params.id}/goods`)"
                    >Назад к списку
                    </CButton>
                </CCardFooter>
            </CCard>
        </CCol>
    </CRow>
</template>

<script>
    import to from 'await-to-js';
    import { morphism } from 'morphism';
    import { cilCheckCircle } from '@coreui/icons';

    import GoodsTabLinks from '@/components/grocers/GoodsTabLinks';

    export default {
        components: {
            GoodsTabLinks,
        },

        data() {
            return {
                cilCheckCircle,
                entity: undefined,
                schema: {
                    title: 'title',
                    description: 'description',
                    main_picture: 'main_picture',
                    price: 'price',
                    vat: 'vat',
                    weight_unit: 'weight_unit',
                    weight_step: 'weight_step',
                    weight: 'weight',
                    cert_required: source => source.cert_required === undefined ? null : source.cert_required,
                    cert_urls: source =>
                        Array.isArray(source.cert_urls) ? source.cert_urls : [],
                    quantity: 'quantity',
                    grocer_id: () => this.$route.params.id,
                    position: source => (parseInt(source.position) ? source.position : 1),
                    pictures: source =>
                        Array.isArray(source.pictures) ? source.pictures : [],
                    popular: source => source.popular || false,
                    enabled: source => source.enabled || false,
                },
                vatOptions: [
                    { value: null, label: 'НДС не облагается' },
                    { value: 0, label: 'НДС 0%' },
                    { value: 5, label: 'НДС 5%' },
                    { value: 7, label: 'НДС 7%' },
                    { value: 10, label: 'НДС 10%' },
                    { value: 20, label: 'НДС 20%' },
                    { value: 22, label: 'НДС 22%' },
                    { value: 105, label: 'Расчетный НДС 5/105' },
                    { value: 107, label: 'Расчетный НДС 7/107' },
                    { value: 110, label: 'Расчетный НДС 10/110' },
                    { value: 120, label: 'Расчетный НДС 20/120' },
                    { value: 12, label: 'НДС 12% (только для онлайн-касс в Казахстане)' },
                ],
                unitCodeTitle: {
                    0: 'шт',
                    10: 'г',
                    11: 'кг',
                    255: '',
                }
            };
        },

        computed: {
            certRequiredTitle() {
                if (this.entity.cert_required === true) {
                    return 'Сертификация обязательна';
                }
                if (this.entity.cert_required === false) {
                    return 'Не подлежит сертификации';
                }
                return 'Не указано';
            },
            vat() {
                return this.vatOptions.find(item => item.value === this.entity.vat).label;
            },
        },

        async created() {
            await this.fetch();
        },

        methods: {
            async fetch() {
                const { id, goods_id } = this.$route.params;

                const [error, result] = await to(
                    this.axios.get(`/grocers/${id}/goods/${goods_id}`)
                );
                this.entity = morphism(this.schema, result.data);

                if (error) {
                    return this.$root.$emit('showError', 'Невозможно получить товар.');
                }
            },

            getStaticPath(filename = '') {
                if (filename.startsWith('http')) {
                    return filename;
                }

                return `${process.env.VUE_APP_STATIC_BASE_URL}/${filename}`;
            },
        },
    };
</script>
<style scoped lang="scss">

</style>
